import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const ContactForm = (props) => {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const requestedProduct = searchParams.get('product') || '';
    const isKa = i18n.language === 'ka';

    const defaultMessage = requestedProduct 
        ? (isKa 
            ? `მოგესალმებით, მსურს დემო ჩვენების ჩანიშვნა და კომერციული შეთავაზების მიღება პროდუქტზე: "${requestedProduct}".`
            : `Hello, I would like to request a live demo presentation and quote for: "${requestedProduct}".`)
        : '';

    const [forms, setForms] = useState({
        name: '',
        email: '',
        phone: '',
        message: defaultMessage
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (requestedProduct) {
            setForms(prev => {
                if (prev.message) return prev;
                return {
                    ...prev,
                    message: isKa 
                        ? `მოგესალმებით, მსურს დემო ჩვენების ჩანიშვნა და კომერციული შეთავაზების მიღება პროდუქტზე: "${requestedProduct}".`
                        : `Hello, I would like to request a live demo presentation and quote for: "${requestedProduct}".`
                };
            });
        }
    }, [requestedProduct, isKa]);

    const CONTACT_API_URL = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL + 'contacts.php' : 'https://orbitronix.ge/api/contacts.php';
    const RECAPTCHA_SITE_KEY = '6LcUPFEtAAAAAIa-1gqL2ziVSccxdhdZu40VJjM3';

    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js';
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, []);

    const changeHandler = e => {
        setForms({ ...forms, [e.target.name]: e.target.value });
        setError('');
    };

    const validateForm = () => {
        if (!forms.name.trim()) {
            setError(t('contactForm.errors.nameRequired'));
            return false;
        }
        
        if (!forms.email.trim()) {
            setError(t('contactForm.errors.emailRequired'));
            return false;
        }
        
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(forms.email)) {
            setError(t('contactForm.errors.emailInvalid'));
            return false;
        }
        
        if (!forms.phone.trim()) {
            setError(t('contactForm.errors.phoneRequired'));
            return false;
        }
        
        if (!forms.message.trim()) {
            setError(t('contactForm.errors.messageRequired'));
            return false;
        }
        
        const recaptchaResponse = window.grecaptcha && window.grecaptcha.getResponse();
        if (!recaptchaResponse) {
            setError(t('contactForm.errors.recaptchaRequired'));
            return false;
        }
        
        return true;
    };

    const submitHandler = async e => {
        e.preventDefault();
        
        if (!validateForm()) return;
        
        setIsSubmitting(true);
        setError('');

        try {
            const recaptchaResponse = window.grecaptcha && window.grecaptcha.getResponse();
            let finalMessage = forms.message;
            if (requestedProduct && !finalMessage.includes('[დემო / შეთავაზება')) {
                finalMessage = `[დემო / შეთავაზების მოთხოვნა: ${requestedProduct}]\n\n${forms.message}`;
            }

            const response = await fetch(CONTACT_API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    ...forms, 
                    message: finalMessage,
                    product: requestedProduct,
                    request_type: requestedProduct ? 'demo' : 'contact',
                    recaptcha_response: recaptchaResponse 
                })
            });

            const result = await response.json();

            if (result.success) {
                setIsSubmitted(true);
                setForms({ name: '', email: '', phone: '', message: '' });
                
                if (window.grecaptcha) {
                    window.grecaptcha.reset();
                }
                
                setTimeout(() => {
                    setIsSubmitted(false);
                }, 5000);
            } else {
                throw new Error(result.error || 'Submission failed');
            }
        } catch (error) {
            console.error('Submission error:', error);
            setError(t('contactForm.errors.submissionFailed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={submitHandler} className="contact-form">
            {requestedProduct && (
                <div 
                    className="p-3 mb-4 rounded-3 d-flex align-items-center gap-3"
                    style={{ 
                        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.18) 0%, rgba(139, 92, 246, 0.12) 100%)', 
                        border: '1px solid rgba(99, 102, 241, 0.45)',
                        boxShadow: '0 4px 20px rgba(99, 102, 241, 0.15)'
                    }}
                >
                    <div 
                        className="rounded-circle d-flex align-items-center justify-content-center text-white"
                        style={{ width: '42px', height: '42px', background: '#6366f1', flexShrink: 0 }}
                    >
                        <i className="fas fa-laptop-code fs-6" />
                    </div>
                    <div>
                        <div className="fw-bold text-white small">
                            {isKa ? 'დემო ჩვენების & კომერციული შეთავაზების მოთხოვნა' : 'Request Demo & Commercial Quote'}
                        </div>
                        <div className="small text-info">
                            {isKa ? 'არჩეული პროდუქტი:' : 'Requested Product:'} <strong>{requestedProduct}</strong>
                        </div>
                    </div>
                </div>
            )}
            <div className="row">
                <div className="col-lg-6">
                    <div className="input-field">
                        <label htmlFor="text1">{t('contactForm.name')}</label>
                        <div className="input-box">
                            <input
                                value={forms.name}
                                type="text"
                                name="name"
                                className="form-control"
                                placeholder={t('contactForm.name')}
                                onChange={changeHandler}
                                required
                            />
                        </div>
                    </div>
                </div>
                <div className="col-lg-6">
                    <div className="input-field">
                        <label htmlFor="text2">{t('contactForm.email')}</label>
                        <div className="input-box">
                            <input
                                value={forms.email}
                                type="email"
                                name="email"
                                className="form-control"
                                placeholder={t('contactForm.email')}
                                onChange={changeHandler}
                                required
                            />
                        </div>
                    </div>
                </div>
                <div className="col-lg-12">
                    <div className="input-field">
                        <label htmlFor="text3">{t('contactForm.phone')}</label>
                        <div className="input-box">
                            <input
                                value={forms.phone}
                                type="tel"
                                name="phone"
                                className="form-control"
                                placeholder={t('contactForm.phone')}
                                onChange={changeHandler}
                                required
                            />
                        </div>
                    </div>
                </div>
                <div className="col-lg-12">
                    <div className="input-field text-field">
                        <label htmlFor="text5">{t('contactForm.message')}</label>
                        <div className="input-box">
                            <textarea
                                onChange={changeHandler}
                                value={forms.message}
                                name="message"
                                className="form-control"
                                placeholder={t('contactForm.messagePlaceholder')}
                                required
                            ></textarea>
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="mt-20">
                <div
                    className="g-recaptcha"
                    data-sitekey={RECAPTCHA_SITE_KEY}
                    data-theme="dark"
                ></div>
            </div>
            
            {error && (
                <div className="error-message mt-20" style={{ 
                    color: '#e74c3c', 
                    padding: '10px', 
                    backgroundColor: '#f8d7da', 
                    borderRadius: '4px',
                    border: '1px solid #f5c6cb'
                }}>
                    {error}
                </div>
            )}
            
            {isSubmitted && (
                <div className="success-message mt-20" style={{ 
                    color: '#155724', 
                    padding: '10px', 
                    backgroundColor: '#d4edda', 
                    borderRadius: '4px',
                    border: '1px solid #c3e6cb'
                }}>
                    {t('contactForm.success')}
                </div>
            )}

            <div className="cp-det-btn mt-20 d-grid">
                <button 
                    className="cp-btn" 
                    type='submit'
                    disabled={isSubmitting}
                >
                    {isSubmitting ? t('contactForm.submitting') : t('contactForm.submit')} 
                    <i className="fal fa-arrow-right"></i>
                </button>
            </div>
        </form>
    );
};

export default ContactForm;